/* eslint-disable no-debugger */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

// import { createObservableDataAction, IAction, ICreateActionContext } from '@msdyn365-commerce/core';
// import { IArticle } from './DataServiceEntities.g';

import {
    createObservableDataAction,
    IAction,
    ICommerceApiSettings,
    ICreateActionContext,
    IActionInput,
    IActionContext
} from '@msdyn365-commerce/core';
import { IQueryResultSettings, convertToString, SortColumn, CacheType, AsyncResult } from '@msdyn365-commerce/retail-proxy';
import { IArticle } from './DataServiceEntities.g';
import { getAllArticlesAsync } from './DataActionExtension.g';
import { ILobasTableRefinerValue } from '../modules/components/lobas-table';
import { generateCacheKey, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { toJS } from 'mobx';

import { hydrateRefinersFromUrl } from './lobas-url-utils';

export class BaseCollectionInput implements IActionInput {
    public refiners: ILobasTableRefinerValue[];

    public queryResultSettings: IQueryResultSettings;

    public apiSettings: ICommerceApiSettings;

    public locale?: string;

    public articlePageURL?: string;

    public constructor(
        apiSettings: ICommerceApiSettings,
        queryResultSettings: IQueryResultSettings,
        refiners: ILobasTableRefinerValue[],
        locale?: string,
        articlePageURL?: string
    ) {
        this.apiSettings = apiSettings;
        this.queryResultSettings = queryResultSettings;
        this.refiners = refiners;
        this.locale = locale;
        this.articlePageURL = articlePageURL;
    }

    public getCacheKey = () => {
        const queryResultSettings = {
            ...this.queryResultSettings.Paging,
            ...toJS(this.queryResultSettings.Sorting)
        };

        const cacheKey = generateCacheKey(
            this.apiSettings,
            {
                key: 'Refiners',
                value: this.refiners.map(
                    refiner =>
                        `RefinerRecordId:${refiner.RefinerRecordId}-LeftValueBoundString:${refiner.LeftValueBoundString ||
                            ''}-RightValueBoundString:${refiner.RightValueBoundString || ''}`
                )
            },
            { key: 'QueryResultSettings', value: convertToString(queryResultSettings) },
            { key: 'Locale', value: this.locale }
        );
        return cacheKey;
    };

    public getCacheObjectType = () => 'CollectionActionResult';

    public dataCacheType = (): CacheType => 'request';
}

export class GetFullArticlesByCollectionInput extends BaseCollectionInput implements IActionInput {
    /**
     * The cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'FullArticlesResult';

    /**
     * The data cache type.
     * @returns The data cache type.
     */
    public dataCacheType = (): CacheType => {
        if (
            (this.refiners && this.refiners.length > 0) ||
            (this.queryResultSettings &&
                this.queryResultSettings.Sorting &&
                this.queryResultSettings.Sorting.Columns &&
                this.queryResultSettings.Sorting.Columns.length > 0)
        ) {
            return 'request';
        }
        return 'application';
    };
}

export type CollectionClassConstructor = new (
    apiSettings: ICommerceApiSettings,
    queryResultSettings: IQueryResultSettings,
    refiners: ILobasTableRefinerValue[],
    locale?: string
) => BaseCollectionInput;

export interface IFullArticleResultsWithCount {
    articles: IArticle[];
    count: number;
}

/**
 * TODO: Use this function to create the input required to make the action call.
 * @param args
 * @param inputClassConstuctor
 */
export const createBaseCollectionInput = (
    args: ICreateActionContext,
    inputClassConstuctor: CollectionClassConstructor
): BaseCollectionInput => {
    const queryResultSettings = QueryResultSettingsProxy.fromInputData(args).QueryResultSettings;
    const queryRefiners = hydrateRefinersFromUrl(args.requestContext);

    if (args.requestContext.query && args.requestContext.query.sorting) {
        queryResultSettings.Sorting = { Columns: <SortColumn[]>JSON.parse(decodeURIComponent(args.requestContext.query.sorting)) };
    }

    return new inputClassConstuctor(args.requestContext.apiSettings, queryResultSettings, queryRefiners, args.requestContext.locale);
};

/**
 * CreateInput function which creates and actionInput used to fetch products for a list page.
 * @param args - The API arguments.
 * @returns IActionInput - The action input.
 */
const createInput = (args: ICreateActionContext<{ itemsPerPage: number; articlePageURL: string }>): IActionInput => {
    const input = createBaseCollectionInput(args, GetFullArticlesByCollectionInput);

    // Set Top
    if (input.queryResultSettings.Paging) {
        input.queryResultSettings.Paging.Top = args.config?.itemsPerPage || 1;
    }

    // Set Skip
    if (input.queryResultSettings.Paging) {
        if (args.requestContext.query?.type === 'lobas-article-collection-mini') {
            input.queryResultSettings.Paging.Skip = 0;
        } else if (args.requestContext.query && args.requestContext.query.skip) {
            input.queryResultSettings.Paging.Skip = +args.requestContext.query.skip;
        }
    }

    input.queryResultSettings.Sorting = { Columns: [{ IsDescending: true, ColumnName: 'DateReleased' }] };

    input.queryResultSettings.count = true;

    input.articlePageURL = args.config && args.config.articlePageURL;

    return input;
};

/**
 * Action function to fetch products for a list page.
 * @param input - The input.
 * @param context - The context.
 * @returns IFullArticleResultsWithCount - The full product search result with count.
 */
async function action(input: GetFullArticlesByCollectionInput, context: IActionContext): Promise<IFullArticleResultsWithCount> {
    const defaultNumber: number = 0;

    const promise: AsyncResult<IArticle[]> = getAllArticlesAsync({
        callerContext: context,
        queryResultSettings: input.queryResultSettings
    });

    let articleResults = await promise;

    const pageName = context.requestContext.url.requestUrl.pathname.split('/').at(1);

    if (pageName === input.articlePageURL?.replace('/', '')) {
        const articleURL = context.requestContext.url.requestUrl.pathname.split('/').at(-1);
        articleResults = articleResults.filter(article => article.URL !== articleURL);
    }

    return { articles: articleResults, count: promise.metadata.count ?? defaultNumber };
}

export const getArticlesDataAction = createObservableDataAction({
    id: 'lobas-article-collection/get-full-articles-by-collection',
    action: action as IAction<IFullArticleResultsWithCount>,
    input: createInput
});

export default getArticlesDataAction;
